import { useCallback, useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import FormatToolbar from "./FormatToolbar.jsx";

const TRACKED_COMMANDS = [
  "bold",
  "italic",
  "underline",
  "strikeThrough",
  "insertUnorderedList",
  "insertOrderedList",
];

export function RichTextComposer({
  value,
  onChange,
  onSend,
  onPaste,
  placeholder = "Nhấn Cmd + Shift + X để định dạng tin nhắn",
  autoFocus = false,
  disabled = false,
}) {
  const editorRef = useRef(null);
  const [activeFormats, setActiveFormats] = useState(() => new Set());
  const [isEmpty, setIsEmpty] = useState(!value);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== el.innerHTML) {
      el.innerHTML = value || "";
      setIsEmpty(!value || el.textContent.trim() === "");
    }
  }, [value]);

  useEffect(() => {
    if (autoFocus && editorRef.current) {
      editorRef.current.focus();
      placeCaretAtEnd(editorRef.current);
    }
  }, [autoFocus]);

  const refreshActive = useCallback(() => {
    if (typeof document === "undefined") return;
    const next = new Set();
    for (const cmd of TRACKED_COMMANDS) {
      try {
        if (document.queryCommandState(cmd)) next.add(cmd);
      } catch {
        /* noop */
      }
    }
    setActiveFormats(next);
  }, []);

  useEffect(() => {
    const handler = () => {
      const el = editorRef.current;
      if (!el) return;
      const sel = document.getSelection();
      if (!sel || !sel.anchorNode) return;
      if (el.contains(sel.anchorNode)) refreshActive();
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, [refreshActive]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    setIsEmpty(el.textContent.trim() === "" && !el.querySelector("img"));
    onChange?.(html);
  }, [onChange]);

  const runCommand = useCallback(
    (cmd, val = null) => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      try {
        document.execCommand(cmd, false, val);
      } catch {
        /* noop */
      }
      refreshActive();
      emitChange();
    },
    [emitChange, refreshActive],
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend?.();
      return;
    }
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      const key = e.key.toLowerCase();
      if (key === "b") {
        e.preventDefault();
        runCommand("bold");
      } else if (key === "i") {
        e.preventDefault();
        runCommand("italic");
      } else if (key === "u") {
        e.preventDefault();
        runCommand("underline");
      } else if (key === "s" && e.shiftKey) {
        e.preventDefault();
        runCommand("strikeThrough");
      } else if (key === "z" && !e.shiftKey) {
        e.preventDefault();
        runCommand("undo");
      } else if ((key === "z" && e.shiftKey) || key === "y") {
        e.preventDefault();
        runCommand("redo");
      }
    }
  };

  const handlePaste = (e) => {
    if (onPaste) {
      const handled = onPaste(e);
      if (handled) return;
    }
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.kind === "file") return;
    }
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
    emitChange();
  };

  const editorMinHeight = isExpanded ? "400px" : "60px";
  const editorMaxHeight = isExpanded ? "70vh" : "200px";

  return (
    <div
      className={`flex flex-col ${
        isExpanded ? "fixed inset-4 z-50 bg-white rounded-lg shadow-2xl" : "flex-1 min-h-0"
      }`}
    >
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex items-end gap-2 p-1.5 flex-1 min-h-0">
          <div className="flex-1 relative self-stretch">
            <div
              ref={editorRef}
              contentEditable={!disabled}
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              aria-label="Soạn tin nhắn"
              spellCheck
              data-empty={isEmpty || undefined}
              onInput={emitChange}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onMouseUp={refreshActive}
              onKeyUp={refreshActive}
              className="w-full bg-transparent focus:outline-none text-[13px] text-gray-800 px-2 py-1 overflow-auto leading-relaxed rich-editor"
              style={{
                minHeight: editorMinHeight,
                maxHeight: editorMaxHeight,
              }}
            />
            {isEmpty && (
              <div className="pointer-events-none absolute top-1 left-2 text-[13px] text-gray-400">
                {placeholder}
              </div>
            )}
          </div>
          <button
            className="h-8 w-8 flex items-center justify-center rounded-full bg-primary text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity shrink-0"
            onClick={onSend}
            disabled={isEmpty}
            aria-label="Gửi tin nhắn"
            title="Gửi (Enter)"
          >
            <Send className="w-3.5 h-3.5" />
          </button>
        </div>
        <FormatToolbar
          activeFormats={activeFormats}
          onCommand={runCommand}
          onToggleExpand={() => setIsExpanded((p) => !p)}
          isExpanded={isExpanded}
        />
      </div>
      <style>{`
        .rich-editor ul { list-style: disc; padding-left: 1.5rem; margin: 0.25rem 0; }
        .rich-editor ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
        .rich-editor li { margin: 0.125rem 0; }
        .rich-editor p { margin: 0.125rem 0; }
        .rich-editor u { text-decoration: underline; }
        .rich-editor s { text-decoration: line-through; }
      `}</style>
    </div>
  );
}

function placeCaretAtEnd(el) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = document.getSelection();
  sel.removeAllRanges();
  sel.addRange(range);
}

export default RichTextComposer;
